/**
 * 1.6.0 the PRISM MCP connector's tools (plan A4 and C1; docs/dev/mcp-1.6.0-build.md): each tool's name, what it
 * says to the assistant, whether it writes, the permissions it needs, and its input and output schemas (zod). Shared
 * by the program (stream M registers them with the SDK; A writes the shot and segment tools) and PRISM (stream T's
 * GET /api/v1/me/capabilities lists the tools a caller may use with mcpToolsFor).
 *
 * Outputs are kept short for the assistant: ids, codes, titles, the few facts that matter, at most 50 rows. Each
 * tool is one or two existing API calls. Never offered: delete, purge, restore or recycle, Access, storage
 * configuration, migrations, sign-in, tokens, or anything through the agent gateway.
 */
import { z } from 'zod';
import type { Permission } from './roles.ts';
import { RoughCutRequestSchema } from './rough-cut.ts';
import { RIGHTS_STATES } from './rights.ts';

const Id = z.string().uuid();
const Limit = (max = 50, def = 20) => z.number().int().min(1).max(max).default(def);
/** Field values by the field's label (as the assistant reads them) or id. */
const FieldValues = z.record(z.string().max(120), z.unknown());

/* ---------------------------------------------------------------- shared output pieces */

/**
 * 1.8.0: an asset's rights in short, while rights management is on (absent while it is off): the state (withdrawn,
 * ended, embargoed, not_cleared, restricted, unknown, cleared), whether they end soon, the dates, restrictions and credit.
 */
export const McpRights = z.object({
  state: z.enum(RIGHTS_STATES),
  endingSoon: z.boolean(),
  from: z.string().nullable(),
  until: z.string().nullable(),
  restrictions: z.string().nullable(),
  credit: z.string().nullable(),
});
export type McpRights = z.infer<typeof McpRights>;

export const McpAssetRow = z.object({
  id: Id,
  code: z.string().nullable(),
  title: z.string(),
  class: z.string().nullable(),
  mediaKind: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  status: z.string(),
  thumbnailUrl: z.string().nullable(),
  /** 1.8.0: while rights management is on. */
  rights: McpRights.optional(),
});
export type McpAssetRow = z.infer<typeof McpAssetRow>;

const Paged = <T extends z.ZodTypeAny>(row: T) => z.object({ items: z.array(row).max(200), total: z.number().int().nullable(), more: z.boolean() });

/* ---------------------------------------------------------------- read tools (A4) */

export const SearchAssetsInput = z.object({
  text: z.string().max(500).default(''),
  class: z.string().max(80).optional(),
  mediaKind: z.enum(['video', 'audio', 'image', 'graphic']).optional(),
  /** Added since (ISO date) or a relative day count. */
  addedSince: z.string().max(40).optional(),
  status: z.enum(['ready', 'processing', 'failed', 'placeholder']).optional(),
  collectionId: Id.optional(),
  limit: Limit(),
}).strict();
export const SearchAssetsOutput = Paged(McpAssetRow);

export const GetAssetInput = z.object({ assetId: Id }).strict();
export const GetAssetOutput = z.object({
  asset: McpAssetRow,
  /** Metadata by field label. */
  metadata: z.record(z.string(), z.unknown()),
  revision: z.number().int(),
  versions: z.array(z.object({ number: z.number().int(), createdAt: z.string(), current: z.boolean() })).max(20),
  masters: z.array(z.object({ label: z.string(), primary: z.boolean() })).max(20),
  qc: z.object({ status: z.string(), errors: z.number().int(), warnings: z.number().int() }).nullable(),
  storage: z.object({ online: z.boolean(), locations: z.array(z.string()).max(20) }),
  shots: z.number().int().nullable(),
  url: z.string(),
});

export const SearchMomentsInput = z.object({ text: z.string().min(1).max(500), assetId: Id.optional(), limit: Limit() }).strict();
export const SearchMomentsOutput = Paged(z.object({
  assetId: Id, assetTitle: z.string(), source: z.enum(['stratum', 'subtitle']), layer: z.string(), timecode: z.string().nullable(), startSeconds: z.number(),
  /** 1.7.2: where the moment ends, so a soundbite has both ends. */
  endSeconds: z.number(),
  text: z.string(),
  /** 1.8.0: the asset's rights while rights management is on. */
  rights: McpRights.optional(),
}));

export const ListCollectionsInput = z.object({ text: z.string().max(200).default(''), limit: Limit() }).strict();
export const ListCollectionsOutput = Paged(z.object({ id: Id, name: z.string(), items: z.number().int(), public: z.boolean() }));

export const GetCollectionItemsInput = z.object({ collectionId: Id, limit: Limit() }).strict();
export const GetCollectionItemsOutput = Paged(McpAssetRow);

export const ListWorkflowsInput = z.object({ subject: z.enum(['asset', 'sequence', 'any']).default('any') }).strict();
export const ListWorkflowsOutput = z.object({ items: z.array(z.object({ id: Id, name: z.string(), subject: z.string(), description: z.string() })).max(200) });

export const GetWorkflowRunInput = z.object({ runId: Id }).strict();
export const GetWorkflowRunOutput = z.object({
  id: Id, workflow: z.string(), status: z.string(), startedAt: z.string().nullable(), finishedAt: z.string().nullable(), error: z.string().nullable(),
  steps: z.array(z.object({ id: z.string(), label: z.string(), status: z.string(), attempts: z.number().int(), message: z.string().nullable() })).max(100),
});

export const ListJobsInput = z.object({ state: z.enum(['failed', 'active', 'all']).default('active'), limit: Limit() }).strict();
export const ListJobsOutput = Paged(z.object({ id: z.string(), kind: z.string(), title: z.string(), state: z.string(), percent: z.number().nullable(), assetId: Id.nullable(), error: z.string().nullable(), at: z.string() }));

export const ListTasksInput = z.object({ state: z.enum(['open', 'done', 'all']).default('open'), limit: Limit() }).strict();
export const ListTasksOutput = Paged(z.object({ id: Id, title: z.string(), state: z.string(), dueAt: z.string().nullable(), assetId: Id.nullable() }));

export const ListApprovalsInput = z.object({ limit: Limit() }).strict();
export const ListApprovalsOutput = Paged(z.object({ id: Id, title: z.string(), requestedBy: z.string(), requestedAt: z.string(), assetId: Id.nullable() }));

export const GetMetadataModelInput = z.object({}).strict();
export const GetMetadataModelOutput = z.object({
  fields: z.array(z.object({ id: z.string(), label: z.string(), type: z.string(), options: z.array(z.string()).max(200).optional(), editable: z.boolean() })).max(500),
  classes: z.array(z.object({ id: z.string(), name: z.string() })).max(100),
});

export const GetShareLinkStatusInput = z.object({ assetId: Id }).strict();
export const GetShareLinkStatusOutput = z.object({ links: z.array(z.object({ id: Id, expiresAt: z.string().nullable(), download: z.boolean(), views: z.number().int(), revoked: z.boolean() })).max(50) });

/* ---------------------------------------------------------------- write tools (A4) */

export const UpdateMetadataInput = z.object({
  assetId: Id,
  /** The revision get_asset gave; a conflict answers the current values. */
  revision: z.number().int(),
  values: FieldValues,
}).strict();
export const UpdateMetadataOutput = z.object({ revision: z.number().int(), changed: z.array(z.string()) });

export const AddToCollectionInput = z.object({ collectionId: Id, assetIds: z.array(Id).min(1).max(50) }).strict();
export const AddToCollectionOutput = z.object({ added: z.number().int(), already: z.number().int() });

export const RunWorkflowInput = z.object({ workflowId: Id, assetIds: z.array(Id).min(1).max(20) }).strict();
export const RunWorkflowOutput = z.object({ runs: z.array(z.object({ assetId: Id, runId: Id })) });

export const ExportAssetInput = z.object({
  assetId: Id,
  /** An export format preset id, or 'original'. */
  preset: z.string().min(1).max(64),
  destinationLocationId: Id.optional(),
  /**
   * 1.8.0: the person has read the asset's rights restrictions and wants to go ahead (the policy's warnings). Blocks,
   * and blocks a rights manager could override, can't be passed through the connector.
   */
  confirmRightsWarnings: z.boolean().optional(),
}).strict();
export const ExportAssetOutput = z.object({ exportId: Id, status: z.string() });

export const CreateTaskInput = z.object({
  title: z.string().min(1).max(500), details: z.string().max(5000).default(''), assetId: Id.optional(),
  assignee: z.union([z.object({ userId: Id }), z.object({ role: z.string().max(40) })]).optional(), dueAt: z.string().max(40).optional(),
}).strict();
export const CreateTaskOutput = z.object({ taskId: Id });

export const CommentOnApprovalInput = z.object({ approvalId: Id, comment: z.string().min(1).max(5000) }).strict();
export const CommentOnApprovalOutput = z.object({ ok: z.literal(true) });

export const CreatePlaceholderInput = z.object({ title: z.string().min(1).max(500), class: z.string().max(80).optional(), metadata: FieldValues.default({}) }).strict();
export const CreatePlaceholderOutput = z.object({ assetId: Id, code: z.string().nullable() });

/* ---------------------------------------------------------------- shots and log layers (C1, stream A) */

export const ListShotsInput = z.object({ assetId: Id, offset: z.number().int().min(0).default(0), limit: Limit(200, 50) }).strict();
export const ListShotsOutput = z.object({
  total: z.number().int(),
  offset: z.number().int(),
  items: z.array(z.object({ index: z.number().int(), start: z.string(), end: z.string(), durationSeconds: z.number(), text: z.string() })).max(200),
});

/** Answered as MCP image content (one per frame) plus a text line per frame (position and timecode). */
export const GetShotFramesInput = z.object({ assetId: Id, shotIndex: z.number().int().min(0), count: z.number().int().min(1).max(3).default(1) }).strict();
export const GetShotFramesOutput = z.object({ frames: z.array(z.object({ position: z.enum(['start', 'middle', 'end']), timecode: z.string(), width: z.number().int(), height: z.number().int() })).max(3) });

export const ListLayersInput = z.object({}).strict();
export const ListLayersOutput = z.object({
  items: z.array(z.object({ id: z.string(), label: z.string(), kind: z.enum(['text', 'vocabulary', 'tags']), options: z.array(z.object({ value: z.string(), label: z.string() })).max(200), system: z.boolean() })).max(200),
});

export const GetSegmentsInput = z.object({ assetId: Id, layerId: z.string().regex(/^s_[a-z0-9_]{1,40}$/), limit: Limit(500, 200) }).strict();
export const GetSegmentsOutput = z.object({
  items: z.array(z.object({ id: Id, start: z.string(), end: z.string(), text: z.string(), values: z.array(z.string()), suggestion: z.boolean(), byPerson: z.boolean() })).max(500),
});

export const WriteSegmentInput = z.object({
  assetId: Id,
  layerId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
  /** By shot (0-based, list_shots' index) or by ticks. */
  shotIndex: z.number().int().min(0).optional(),
  startTicks: z.number().int().nonnegative().optional(),
  endTicks: z.number().int().positive().optional(),
  text: z.string().max(5000).optional(),
  values: z.array(z.string().min(1).max(80)).max(50).optional(),
}).strict()
  .refine((v) => v.shotIndex !== undefined || (v.startTicks !== undefined && v.endTicks !== undefined), 'Give shotIndex, or startTicks and endTicks')
  .refine((v) => v.text !== undefined || v.values !== undefined, 'Give text or values');
export const WriteSegmentOutput = z.object({ segmentId: Id, created: z.boolean(), suggestion: z.literal(true) });

/** Metadata values (by field label or id) as suggestions a person accepts or rejects in the Metadata tab. */
export const SuggestMetadataInput = z.object({
  assetId: Id,
  values: z.record(z.string().min(1).max(80), z.union([z.string().max(5000), z.number(), z.boolean(), z.array(z.string().max(200)).max(50)])),
}).strict().refine((v) => Object.keys(v.values).length > 0, 'Give at least one field');
export const SuggestMetadataOutput = z.object({
  items: z.array(z.object({ fieldId: z.string(), fieldLabel: z.string() })).max(100),
  suggestion: z.literal(true),
});

/* ---------------------------------------------------------------- 1.7.2: collections, transcripts, graphics, rough cuts */

export const CreateCollectionInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).default(''),
  /** A folder to make it in (list_collections' id); it then has that folder's owner and visibility. */
  parentId: Id.optional(),
  /** A public top-level collection (needs “Curate public collections”); default: your own. */
  public: z.boolean().default(false),
}).strict();
export const CreateCollectionOutput = z.object({ collectionId: Id, name: z.string(), parentId: Id.nullable(), public: z.boolean() });

export const GetTranscriptInput = z.object({
  assetId: Id,
  /** The track in this language (a code or a label, e.g. en or English); default the first. */
  language: z.string().min(1).max(40).optional(),
  trackId: Id.optional(),
  fromSeconds: z.number().min(0).optional(),
  toSeconds: z.number().min(0).optional(),
  limit: Limit(2000, 300),
}).strict();
export const GetTranscriptOutput = z.object({
  tracks: z.array(z.object({ id: Id, language: z.string(), label: z.string(), kind: z.string(), cues: z.number().int() })).max(50),
  trackId: Id.nullable(),
  cues: z.array(z.object({ startSeconds: z.number(), endSeconds: z.number(), text: z.string(), speaker: z.string().optional() })).max(2000),
  more: z.boolean(),
});

export const ListGraphicsInput = z.object({ q: z.string().max(200).optional(), limit: Limit() }).strict();
export const ListGraphicsOutput = z.object({
  items: z.array(z.object({
    templateId: z.string(), name: z.string(), kind: z.string(), engine: z.string(),
    /** The aspects it has a layout for (16:9, 9:16…). */
    layouts: z.array(z.string()).max(20),
    defaultSeconds: z.number(),
    /** A sting keeps its own fixed length. */
    sting: z.boolean(),
    fields: z.array(z.object({
      id: z.string(), label: z.string(), kind: z.string(), default: z.union([z.string(), z.number(), z.boolean()]).nullable(),
      options: z.array(z.union([z.string(), z.number(), z.boolean()])).max(200).optional(), maxLength: z.number().int().optional(), required: z.boolean(),
    })).max(100),
  })).max(50),
  more: z.boolean(),
});

/** The rough cut, as POST /api/v1/sequences/rough-cut takes it (rough-cut.ts). */
export const CreateRoughCutInput = RoughCutRequestSchema;
export const CreateRoughCutOutput = z.object({
  sequenceId: Id, name: z.string(), url: z.string(), durationSeconds: z.number(),
  counts: z.object({ clips: z.number().int(), cutaways: z.number().int(), music: z.number().int(), graphics: z.number().int(), markers: z.number().int() }),
  warnings: z.array(z.string()).max(1000),
  collectionId: Id.nullable(),
});

/* ---------------------------------------------------------------- the tool table */

export interface McpToolInfo {
  title: string;
  /** What the assistant reads: what the tool does and, for a write tool, clearly what it will change. */
  description: string;
  write: boolean;
  /** Every one is needed. */
  permissions: readonly Permission[];
  /** write_segment and suggest_metadata also need the token's "Write log descriptions" (writeLog); a session or API key needs only the permissions. */
  needsWriteLog?: boolean;
  input: z.ZodTypeAny;
  output: z.ZodTypeAny;
  /** The stream that builds it (M: the program's tools; A: shots and segments). */
  owner: 'M' | 'A';
}

export const MCP_TOOLS = {
  search_assets: { title: 'Search assets', description: 'Find assets by text and filters (class, media kind, added since, status, collection). Returns up to 50 with id, code, title, class, duration, status and a thumbnail link, and while rights management is on each one’s rights (withdrawn, ended, embargoed, not cleared, restricted, unknown or cleared, with dates and restrictions).', write: false, permissions: ['asset.read'], input: SearchAssetsInput, output: SearchAssetsOutput, owner: 'M' },
  get_asset: { title: 'Get an asset', description: 'One asset: its metadata by field label, revision, versions, masters, QC summary, storage availability and shot count, and while rights management is on its rights (state, dates, restrictions, credit). Withdrawn material must not be used.', write: false, permissions: ['asset.read'], input: GetAssetInput, output: GetAssetOutput, owner: 'M' },
  search_moments: { title: 'Search moments', description: 'Find timecoded moments: log layer segments and subtitle or transcript lines matching the text, with timecodes, and while rights management is on the asset’s rights.', write: false, permissions: ['asset.read'], input: SearchMomentsInput, output: SearchMomentsOutput, owner: 'M' },
  list_collections: { title: 'List collections', description: 'Collections you can see, with their item counts.', write: false, permissions: ['asset.read'], input: ListCollectionsInput, output: ListCollectionsOutput, owner: 'M' },
  get_collection_items: { title: 'Get collection items', description: 'The assets in one collection.', write: false, permissions: ['asset.read'], input: GetCollectionItemsInput, output: GetCollectionItemsOutput, owner: 'M' },
  list_workflows: { title: 'List workflows', description: 'Published workflows you may start, and what they run on.', write: false, permissions: ['automation.run'], input: ListWorkflowsInput, output: ListWorkflowsOutput, owner: 'M' },
  get_workflow_run: { title: 'Get a workflow run', description: 'A workflow run’s status and its steps.', write: false, permissions: ['asset.read'], input: GetWorkflowRunInput, output: GetWorkflowRunOutput, owner: 'M' },
  list_jobs: { title: 'List jobs', description: 'Processing jobs: the active ones or the failed ones, grouped as in Jobs (administrators).', write: false, permissions: ['admin'], input: ListJobsInput, output: ListJobsOutput, owner: 'M' },
  list_tasks: { title: 'List my tasks', description: 'Your tasks (open by default).', write: false, permissions: ['asset.read'], input: ListTasksInput, output: ListTasksOutput, owner: 'M' },
  list_approvals: { title: 'List approvals', description: 'Approvals waiting for you.', write: false, permissions: ['asset.read'], input: ListApprovalsInput, output: ListApprovalsOutput, owner: 'M' },
  get_metadata_model: { title: 'Get the metadata model', description: 'The metadata fields (labels, types, options, whether you may edit them) and asset classes, so values match the model.', write: false, permissions: ['asset.read'], input: GetMetadataModelInput, output: GetMetadataModelOutput, owner: 'M' },
  get_share_link_status: { title: 'Get share links', description: 'An asset’s public share links: expiry, download, views, revoked.', write: false, permissions: ['asset.share'], input: GetShareLinkStatusInput, output: GetShareLinkStatusOutput, owner: 'M' },
  update_metadata: { title: 'Update metadata', description: 'CHANGES an asset’s metadata: sets the given fields (by label or id) on the asset, as you. Needs the revision from get_asset; if the asset changed meanwhile, nothing is changed and the current values are returned.', write: true, permissions: ['asset.edit_metadata'], input: UpdateMetadataInput, output: UpdateMetadataOutput, owner: 'M' },
  add_to_collection: { title: 'Add to a collection', description: 'CHANGES a collection: adds the given assets to it.', write: true, permissions: ['asset.read'], input: AddToCollectionInput, output: AddToCollectionOutput, owner: 'M' },
  run_workflow: { title: 'Run a workflow', description: 'STARTS a published workflow on the given assets (at most 20), as you. The workflow then does whatever its steps do.', write: true, permissions: ['automation.run'], input: RunWorkflowInput, output: RunWorkflowOutput, owner: 'M' },
  export_asset: { title: 'Export an asset', description: 'STARTS an export of an asset in a format preset (or its original format) to an export location. While rights management is on, PRISM refuses material whose rights block it (withdrawn, and what the policy blocks or leaves to a rights manager: those are never overridden through the connector); for restrictions the policy only warns about, tell the person what they are and, if they still want it, call again with confirmRightsWarnings.', write: true, permissions: ['export.create'], input: ExportAssetInput, output: ExportAssetOutput, owner: 'M' },
  create_task: { title: 'Create a task', description: 'CREATES a task for a person or a role, optionally linked to an asset; they are notified.', write: true, permissions: ['asset.read'], input: CreateTaskInput, output: CreateTaskOutput, owner: 'M' },
  comment_on_approval: { title: 'Comment on an approval', description: 'ADDS your comment to an approval (does not approve or reject it).', write: true, permissions: ['asset.read'], input: CommentOnApprovalInput, output: CommentOnApprovalOutput, owner: 'M' },
  create_placeholder: { title: 'Create a placeholder', description: 'CREATES a placeholder asset (no media yet) with a title, class and metadata.', write: true, permissions: ['asset.upload'], input: CreatePlaceholderInput, output: CreatePlaceholderOutput, owner: 'M' },
  // 1.7.2: collections, transcripts, graphics and rough cuts.
  create_collection: { title: 'Create a collection', description: 'CREATES a collection or a subfolder (in parentId): your own, or a public top-level one with public true (that needs “Curate public collections”). add_to_collection fills it.', write: true, permissions: ['asset.read'], input: CreateCollectionInput, output: CreateCollectionOutput, owner: 'M' },
  get_transcript: { title: 'Get a transcript', description: 'The subtitle or transcript lines of an asset’s current version, with start and end seconds and the speaker: the chosen track (trackId, else the language, else the first), optionally between fromSeconds and toSeconds, and the list of tracks. Use it to cut soundbites on whole sentences.', write: false, permissions: ['asset.read'], input: GetTranscriptInput, output: GetTranscriptOutput, owner: 'M' },
  list_graphics: { title: 'List graphics', description: 'The graphics templates a sequence can use (the latest enabled, ready version of each): name, kind, the aspects they have layouts for, default length, whether it is a sting (fixed length), and the fields to fill in (id, label, kind, default, choices, maximum length).', write: false, permissions: ['asset.read'], input: ListGraphicsInput, output: ListGraphicsOutput, owner: 'M' },
  create_rough_cut: { title: 'Create a rough cut', description: 'CREATES a new sequence (a rough cut) for a person to finish in the editor: storyline clips in order (each with an optional note, shown as a marker), cutaways on a muted Cutaways track over them, music on A2, graphics with their fields filled in (list_graphics), and markers. Times are seconds from the start of the media; a place is {seconds} in the sequence or {clip, offsetSeconds} from a storyline clip’s start (clip 1 is the first). Never changes an existing sequence. Answers the editor link and any warnings (ranges clamped, items left out). While rights management is on, sources whose rights block their use are left out with a warning, and restricted ones are named in the warnings.', write: true, permissions: ['sequence.edit', 'asset.read'], input: CreateRoughCutInput, output: CreateRoughCutOutput, owner: 'M' },
  list_shots: { title: 'List shots', description: 'The shots of an asset’s current version (from shot detection), with timecodes and the dialogue inside each shot. Paged.', write: false, permissions: ['asset.read'], input: ListShotsInput, output: ListShotsOutput, owner: 'A' },
  get_shot_frames: { title: 'Get shot frames', description: 'Frames of one shot as images (start, middle, end; 768 px), to describe what is seen.', write: false, permissions: ['asset.read'], input: GetShotFramesInput, output: GetShotFramesOutput, owner: 'A' },
  list_layers: { title: 'List log layers', description: 'The timecoded log layers, with their kinds and vocabularies, so written values are ones the model allows.', write: false, permissions: ['asset.read'], input: ListLayersInput, output: ListLayersOutput, owner: 'A' },
  get_segments: { title: 'Get segments', description: 'The segments of one log layer of an asset; says which are suggestions and which a person wrote.', write: false, permissions: ['asset.read'], input: GetSegmentsInput, output: GetSegmentsOutput, owner: 'A' },
  write_segment: { title: 'Write a segment', description: 'WRITES a description into a log layer for a shot or a span, as a suggestion a person accepts or rejects. Never changes a segment a person wrote or edited: that answers SEGMENT_HUMAN, so leave it.', write: true, permissions: ['asset.edit_metadata'], needsWriteLog: true, input: WriteSegmentInput, output: WriteSegmentOutput, owner: 'A' },
  suggest_metadata: { title: 'Suggest metadata', description: 'SUGGESTS metadata values for an asset (fields by label or id), e.g. a summary of the whole asset. Nothing is changed directly: each value waits as a suggestion a person accepts or rejects. A newer suggestion for the same field replaces the pending one.', write: true, permissions: ['asset.edit_metadata'], needsWriteLog: true, input: SuggestMetadataInput, output: SuggestMetadataOutput, owner: 'A' },
} as const satisfies Record<string, McpToolInfo>;
export type McpToolName = keyof typeof MCP_TOOLS;
export const MCP_TOOL_NAMES = Object.keys(MCP_TOOLS) as McpToolName[];

/** The tools a caller may use (GET /api/v1/me/capabilities): its permissions, a read-only token, writeLog. */
export function mcpToolsFor(caller: { permissions: readonly string[]; readOnly: boolean; writeLog: boolean; viaToken: boolean }): McpToolName[] {
  return MCP_TOOL_NAMES.filter((name) => {
    const t: McpToolInfo = MCP_TOOLS[name];
    if (!t.permissions.every((p) => caller.permissions.includes(p))) return false;
    if (t.write && caller.readOnly) return false;
    if (t.needsWriteLog && caller.viaToken && !caller.writeLog) return false;
    return true;
  });
}

/* ---------------------------------------------------------------- resources and prompts */

/** `prism://asset/{id}`: the asset summary as text. */
export const MCP_ASSET_RESOURCE = 'prism://asset/{id}';

/** The describe_asset prompt (C1, A): list shots, fetch frames, write one description per shot, then a summary suggestion. */
export const DescribeAssetPromptArgs = z.object({
  assetId: Id,
  layerId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
  language: z.string().min(2).max(40).default('English'),
  /** A field for the whole-asset summary, written as a metadata suggestion; none: no summary. */
  summaryFieldId: z.string().max(80).optional(),
}).strict();
/** 1.7.2 the rough_cut prompt (M): find the media, read the transcripts, look at the shots, then create_rough_cut. Prompt arguments are text. */
export const RoughCutPromptArgs = z.object({
  brief: z.string().min(1).max(4000),
  /** The length to aim for, in seconds. */
  targetSeconds: z.string().regex(/^\d{1,5}$/).optional(),
  collectionId: Id.optional(),
  /** A sequence format id (hd1080-25, vertical-25…); default 1920×1080. */
  format: z.string().max(40).optional(),
}).strict();
export const MCP_PROMPTS = {
  describe_asset: { title: 'Describe an asset', args: DescribeAssetPromptArgs, owner: 'A' },
  rough_cut: { title: 'Make a rough cut', args: RoughCutPromptArgs, owner: 'M' },
} as const;

/** Short sentences the program gives the assistant for PRISM's error codes (anything else: the API's message). */
export const MCP_ERROR_TEXT: Record<string, string> = {
  FORBIDDEN: 'You don’t have permission to do that in PRISM.',
  TOKEN_READ_ONLY: 'This connection is read-only, so it cannot change anything in PRISM.',
  TOKEN_SCOPE: 'Assistant connections cannot use that part of PRISM.',
  TOKEN_INVALID: 'The PRISM token is unknown, revoked or expired. Make a new one in My account → AI assistant access and run prism-mcp setup again.',
  ASSISTANT_ACCESS_OFF: 'AI assistant access is off in this PRISM workspace.',
  TOKEN_RATE_LIMITED: 'Too many requests to PRISM; wait a moment and try again.',
  TOKEN_WRITE_LOG_REQUIRED: 'This connection may not write log descriptions (turn on “Write log descriptions” for the token).',
  REVISION_CONFLICT: 'That asset changed meanwhile; read it again (get_asset) and retry with the new revision.',
  SEGMENT_HUMAN: 'A person wrote or edited that segment; leave it as it is.',
  NOT_FOUND: 'PRISM has no such item, or you cannot see it.',
  NO_SHOTS: 'The asset has no shots yet; run Detect shots on it first.',
  NO_PROXY: 'The asset has no proxy yet.',
  // 1.7.2.
  NO_MEDIA: 'The asset has no media yet (a placeholder), so it has no transcript or picture.',
  READ_ONLY: 'You can look at that collection but not change it.',
  // 1.8.0 rights.
  RIGHTS_CONFIRMATION_REQUIRED: 'The asset has rights restrictions: tell the person what they are, and only if they still want it, try again with confirmRightsWarnings.',
  RIGHTS_OVERRIDE_REQUIRED: 'The asset’s rights block this; only a rights manager can go past that, in PRISM itself (never through the connector).',
  RIGHTS_MANAGE_REQUIRED: 'Changing rights fields needs the Manage rights permission in PRISM.',
  RIGHTS_WITHDRAWN: 'The asset has been withdrawn and must not be used.',
};

/* ---------------------------------------------------------------- 1.8.1 the routes a token may use */

export type McpTokenRouteMethod = 'GET' | 'POST' | 'PATCH' | 'PUT';

/**
 * 1.8.1: the only routes a personal assistant token (`pmcp_`) may use, by method and Fastify route pattern
 * (req.routeOptions.url): exactly what the PRISM MCP connector (services/mcp) calls, its tools and `prism-mcp setup` /
 * `status` (GET /api/v1/me, /api/v1/me/capabilities). Any other route answers 403 TOKEN_SCOPE (gating.ts); HEAD goes
 * with GET. A new connector call needs its route here (services/mcp/test/token-routes.test.ts checks every call).
 */
export const MCP_TOKEN_ROUTES: ReadonlyArray<{ readonly method: McpTokenRouteMethod; readonly route: string }> = [
  // setup, status and the tool list
  { method: 'GET', route: '/api/v1/me' },
  { method: 'GET', route: '/api/v1/me/capabilities' },
  // the metadata model and classes
  { method: 'GET', route: '/api/v1/metadata-schema' },
  { method: 'GET', route: '/api/v1/asset-classes' },
  // assets
  { method: 'GET', route: '/api/v1/assets' },
  { method: 'POST', route: '/api/v1/assets' },
  { method: 'GET', route: '/api/v1/assets/:id' },
  { method: 'PATCH', route: '/api/v1/assets/:id/metadata' },
  { method: 'GET', route: '/api/v1/assets/:id/versions' },
  { method: 'GET', route: '/api/v1/assets/:id/qc' },
  { method: 'GET', route: '/api/v1/assets/:id/shares' },
  { method: 'GET', route: '/api/v1/assets/:id/subtitles' },
  { method: 'GET', route: '/api/v1/assets/:id/shots' },
  { method: 'GET', route: '/api/v1/assets/:id/shots/:index/frames' },
  { method: 'GET', route: '/api/v1/assets/:id/segments' },
  { method: 'POST', route: '/api/v1/assets/:id/segments/suggest' },
  { method: 'POST', route: '/api/v1/assets/:id/metadata-suggestions' },
  { method: 'POST', route: '/api/v1/assets/:id/exports' },
  { method: 'GET', route: '/api/v1/moments' },
  { method: 'GET', route: '/api/v1/strata' },
  // collections
  { method: 'GET', route: '/api/v1/collections' },
  { method: 'POST', route: '/api/v1/collections' },
  { method: 'GET', route: '/api/v1/collections/:id/items' },
  { method: 'POST', route: '/api/v1/collections/:id/items' },
  // workflows and jobs
  { method: 'GET', route: '/api/v1/automations/runnable' },
  { method: 'POST', route: '/api/v1/automations/:id/runs/batch' },
  { method: 'GET', route: '/api/v1/automation-runs/:id' },
  { method: 'GET', route: '/api/v1/system/jobs' },
  // tasks and approvals
  { method: 'GET', route: '/api/v1/tasks' },
  { method: 'POST', route: '/api/v1/tasks' },
  { method: 'GET', route: '/api/v1/approvals' },
  { method: 'POST', route: '/api/v1/approvals/:id/notes' },
  // graphics and rough cuts (1.7.2)
  { method: 'GET', route: '/api/v1/graphics/templates' },
  { method: 'POST', route: '/api/v1/sequences/rough-cut' },
];

const MCP_TOKEN_ROUTE_KEYS: ReadonlySet<string> = new Set(MCP_TOKEN_ROUTES.map((r) => `${r.method} ${r.route}`));

const asGet = (method: string) => (method.toUpperCase() === 'HEAD' ? 'GET' : method.toUpperCase());

/** Whether a token may use this method on this route pattern (HEAD as GET). */
export function mcpTokenRouteAllowed(method: string, route: string): boolean {
  return MCP_TOKEN_ROUTE_KEYS.has(`${asGet(method)} ${route}`);
}

/** The route pattern a concrete path (query ignored) matches for this method, or null (the connector's tests). */
export function mcpTokenRouteFor(method: string, path: string): string | null {
  const m = asGet(method);
  const segs = path.split('?')[0]!.split('/');
  for (const r of MCP_TOKEN_ROUTES) {
    if (r.method !== m) continue;
    const parts = r.route.split('/');
    if (parts.length === segs.length && parts.every((p, i) => (p.startsWith(':') ? segs[i] !== '' : p === segs[i]))) return r.route;
  }
  return null;
}
